const mobileNavigationInstances = new Map();

class MobileNavigation {
  constructor(header) {
    this.header = header;
    this.toggle = header.querySelector('[data-mobile-navigation-toggle]');
    this.panel = header.querySelector('[data-mobile-navigation]');
    this.content = header.querySelector('[data-mobile-navigation-content]');
    this.closeButton = header.querySelector('[data-mobile-navigation-close]');
    this.desktopQuery = window.matchMedia('(min-width: 64rem)');
    this.inertElements = new Map();

    if (!this.toggle || !this.panel || !this.content || !this.closeButton) return;

    this.handleToggle = () => this.togglePanel();
    this.handleClose = () => this.close({ restoreFocus: true });
    this.handlePanelClick = event => {
      if (event.target === this.panel) this.close({ restoreFocus: true });
    };
    this.handleKeydown = event => this.onKeydown(event);
    this.handleBreakpoint = event => {
      if (event.matches) this.close({ restoreFocus: false });
    };

    this.toggle.addEventListener('click', this.handleToggle);
    this.closeButton.addEventListener('click', this.handleClose);
    this.panel.addEventListener('click', this.handlePanelClick);
    document.addEventListener('keydown', this.handleKeydown);
    this.desktopQuery.addEventListener('change', this.handleBreakpoint);
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  get focusableElements() {
    return Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter(element => !element.hidden);
  }

  togglePanel() {
    if (this.isOpen) {
      this.close({ restoreFocus: true });
    } else {
      this.open();
    }
  }

  open() {
    this.panel.hidden = false;
    this.panel.setAttribute('aria-hidden', 'false');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.toggle.setAttribute('aria-label', this.toggle.dataset.closeLabel);
    document.body.classList.add('mobile-navigation-open');
    this.setBackgroundInert(true);

    const firstNavigationLink = this.panel.querySelector('.mobile-navigation__list a[href]');
    (firstNavigationLink || this.closeButton).focus();
  }

  close({ restoreFocus }) {
    if (!this.isOpen) return;

    this.panel.hidden = true;
    this.panel.setAttribute('aria-hidden', 'true');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-label', this.toggle.dataset.openLabel);
    document.body.classList.remove('mobile-navigation-open');
    this.setBackgroundInert(false);

    if (restoreFocus && this.toggle.isConnected) this.toggle.focus();
  }

  setBackgroundInert(makeInert) {
    if (makeInert) {
      const headerContainer = Array.from(document.body.children)
        .find(element => element === this.header || element.contains(this.header));
      const elements = Array.from(document.body.children)
        .filter(element => element !== headerContainer);
      const headerInner = this.header.querySelector('.site-header__inner');
      if (headerInner) elements.push(headerInner);

      elements.forEach(element => {
        this.inertElements.set(element, element.inert);
        element.inert = true;
      });
      return;
    }

    this.inertElements.forEach((wasInert, element) => {
      if (element.isConnected) element.inert = wasInert;
    });
    this.inertElements.clear();
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = this.focusableElements;
    if (focusableElements.length === 0) {
      event.preventDefault();
      this.closeButton.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  destroy() {
    this.close({ restoreFocus: false });
    this.toggle.removeEventListener('click', this.handleToggle);
    this.closeButton.removeEventListener('click', this.handleClose);
    this.panel.removeEventListener('click', this.handlePanelClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.desktopQuery.removeEventListener('change', this.handleBreakpoint);
  }
}

function initialiseMobileNavigation(root = document) {
  const headers = root.matches?.('[data-site-header]')
    ? [root]
    : root.querySelectorAll('[data-site-header]');

  headers.forEach(header => {
    if (mobileNavigationInstances.has(header)) return;

    const toggle = header.querySelector('[data-mobile-navigation-toggle]');
    const panel = header.querySelector('[data-mobile-navigation]');
    const content = header.querySelector('[data-mobile-navigation-content]');
    const closeButton = header.querySelector('[data-mobile-navigation-close]');

    if (!toggle || !panel || !content || !closeButton) return;

    mobileNavigationInstances.set(header, new MobileNavigation(header));
  });
}

initialiseMobileNavigation();

document.addEventListener('shopify:section:load', event => {
  initialiseMobileNavigation(event.target);
});

document.addEventListener('shopify:section:unload', event => {
  const headers = event.target.matches?.('[data-site-header]')
    ? [event.target]
    : event.target.querySelectorAll('[data-site-header]');

  headers.forEach(header => {
    const instance = mobileNavigationInstances.get(header);
    if (!instance) return;
    instance.destroy();
    mobileNavigationInstances.delete(header);
  });
});
